/** Typed fetch wrapper for the Cloud Migration Hub API (same origin, cookie session). */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

export async function api<T = unknown>(method: string, path: string, body?: unknown, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: body !== undefined && !(body instanceof ArrayBuffer) && !(body instanceof Blob) ? { "content-type": "application/json" } : {},
    body: body === undefined ? undefined : body instanceof ArrayBuffer || body instanceof Blob ? body : JSON.stringify(body),
    ...init,
  });
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) {
    const err = (data as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error;
    throw new ApiError(res.status, err?.code ?? "error", err?.message ?? `Request failed (${res.status})`, err?.details);
  }
  return data as T;
}

function safeJson(t: string): unknown {
  try {
    return JSON.parse(t);
  } catch {
    return t;
  }
}

export const get = <T>(p: string) => api<T>("GET", p);
export const post = <T>(p: string, b?: unknown) => api<T>("POST", p, b ?? {});
export const patch = <T>(p: string, b: unknown) => api<T>("PATCH", p, b);
export const put = <T>(p: string, b: unknown) => api<T>("PUT", p, b);
export const del = <T>(p: string) => api<T>("DELETE", p);

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === "validation_error" && Array.isArray(e.details)) return (e.details as Array<{ path: string; message: string }>).map((d) => `${d.path}: ${d.message}`).join("; ");
    return e.message;
  }
  return e instanceof Error ? e.message : "Something went wrong";
}

// ---------------------------------------------------------------- shared types (mirror the API)

export type Role = "owner" | "admin" | "operator" | "viewer" | "billing";

export interface Me {
  user: { id: string; email: string; name: string; isPlatformAdmin: boolean; emailVerified: boolean; mfaEnabled: boolean; mustChangePassword: boolean };
  workspaces: Array<{ id: string; name: string; region: string; partnerId: string | null; role: Role; via: "member" | "partner" }>;
  partners: Array<{ id: string; name: string; role: "partner_admin" | "partner_operator" }>;
  sessionState: "ok" | "mfa_required" | "password_change_required" | "mfa_enrolment_required";
}

export interface WorkspaceDetail {
  workspace: { id: string; name: string; region: string; retention: string; logoUrl: string | null; partner: { id: string; name: string } | null; createdAt: number };
  role: Role;
  via: string;
  permissions: string[];
  stats: { connections: number; migrations: number; runs: number; storageBytes: number; links: number };
  balance: number | null;
}

export interface Connection {
  id: string;
  kind: "csv" | "klaviyo" | "mailchimp" | "shopify";
  name: string;
  readOnly: boolean;
  status: string;
  accountLabel: string | null;
  lastTestedAt: number | null;
  shop?: string;
  createdAt: number;
}

export interface CredentialField {
  name: string;
  label: string;
  secret: boolean;
  placeholder?: string;
  help?: string;
}

export interface Catalog {
  kinds: Array<{ kind: Connection["kind"]; label: string; roles: Array<"source" | "target">; credentialFields: CredentialField[] }>;
  kits: Array<{
    id: string;
    name: string;
    description: string;
    source: "csv" | "mailchimp" | "shopify";
    target: "klaviyo" | "shopify";
    entities: Array<{ entity: string; label: string; notes?: string; expectedColumns: string[]; mapping: MappingSpec }>;
  }>;
  shopifyOAuth: boolean;
}

export interface Transform {
  op: string;
  [k: string]: unknown;
}

export interface FieldMapping {
  target: string;
  source?: string | string[];
  transforms: Transform[];
}

export interface MappingSpec {
  entity: string;
  keyField: string;
  requireKey: boolean;
  fields: FieldMapping[];
}

export interface Migration {
  id: string;
  name: string;
  kitId: string;
  entity: string;
  sourceConnectionId: string;
  sourceObjectId: string;
  targetConnectionId: string;
  mapping: MappingSpec;
  options: { failureThreshold?: number };
  status: string;
  createdAt: number;
  updatedAt: number;
}

export interface DiffSummary {
  total: number;
  create: number;
  update: number;
  unchanged: number;
  skip: number;
  error: number;
  consentProtected: number;
  notRevertible: number;
  partiallyRevertible: number;
}

export interface Run {
  id: string;
  migrationId: string;
  kind: "dryrun" | "apply" | "revert" | "stage";
  status: "queued" | "running" | "completed" | "failed" | "cancelled";
  preview: boolean;
  parentRunId: string | null;
  startedAt: number | null;
  finishedAt: number | null;
  summary: {
    diff?: DiffSummary;
    counts?: Record<string, number | Record<string, number>>;
    preview?: boolean;
    truncated?: boolean;
    reversibility?: { create: string; update: string; note?: string };
    outcomes?: Record<string, number>;
    stoppedReason?: string | null;
  } | null;
  progress: Record<string, unknown> | null;
  creditsReserved: number;
  creditsUsed: number;
  error: string | null;
  createdAt: number;
}

export interface DiffEntry {
  key: string;
  action: "create" | "update" | "unchanged" | "skip" | "error";
  changes: Array<{ field: string; before: unknown; after: unknown }>;
  reason?: string;
  reversible: "full" | "partial" | "none";
  protectedConsent?: Array<{ channel: string; reason: string }>;
}

export interface LedgerEntry {
  id: number;
  at: number;
  type: string;
  amount: number;
  balance_after: number;
  ref_type: string | null;
  ref_id: string | null;
  description: string | null;
}

export interface Pricing {
  placeholder: boolean;
  rates: Record<string, number>;
  freePreviewRecords: number;
  bundles: Array<{ id: string; credits: number; priceCents: number }>;
  packs: Array<{ id: string; name: string; kitIds: string[]; maxRecords: number; priceCents: number }>;
  defaultWholesaleDiscountPct: number;
}
